// src/pages/Results2026.js - 2026 Race Results with User Predictions
import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import apiClient from '../utils/apiClient';

function Results2026() {
  const authContext = useContext(AuthContext);
  const currentUser = authContext?.user;
  const { raceId } = useParams();

  const [races, setRaces] = useState([]);
  const [selectedRace, setSelectedRace] = useState(null);
  const [raceResults, setRaceResults] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resultsLoading, setResultsLoading] = useState(false);

  const formatAustralianDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return `${String(date.getDate()).padStart(2,'0')}/${String(date.getMonth()+1).padStart(2,'0')}/${date.getFullYear()}`;
  };

  const arePredictionsClosed = (race) => {
    if (!race) return false;
    const now = new Date();
    if (new Date(race.date) < now) return true;
    if (race.qualifyingDateTime) {
      const closingTime = new Date(new Date(race.qualifyingDateTime).getTime() - 2 * 60 * 1000);
      if (closingTime < now) return true;
    }
    return false;
  };

  const areSprintPredictionsClosed = (race) => {
    if (!race) return false;
    const now = new Date();
    if (new Date(race.date) < now) return true;
    if (race.sprintQualifyingDateTime) {
      const closingTime = new Date(new Date(race.sprintQualifyingDateTime).getTime() - 2 * 60 * 1000);
      if (closingTime < now) return true;
    }
    return false;
  };

  /**
   * Normalise scoreBreakdown into an array of breakdown items for the given
   * race type. scoreBreakdown can be one of several shapes depending on how
   * (and when) it was written:
   *
   *   2026 object:  { sprint: [...], feature: [...] }
   *   2025 array:   [ { type: 'feature', breakdown: [...] }, ... ]
   *   undefined/null when the race hasn't been scored yet
   *
   * Returns an array of breakdown items (each like { item, total }) for the
   * requested raceType, or [] when nothing is available. Defensive: never
   * throws regardless of the shape it receives.
   */
  const getBreakdownItems = (scoreBreakdown, raceType) => {
    if (!scoreBreakdown) return [];

    // 2026 object shape: { sprint: [...], feature: [...] }
    if (!Array.isArray(scoreBreakdown) && typeof scoreBreakdown === 'object') {
      const arr = scoreBreakdown[raceType];
      return Array.isArray(arr) ? arr : [];
    }

    // 2025 array shape: [ { type, breakdown: [...] }, ... ]
    if (Array.isArray(scoreBreakdown)) {
      const entry = scoreBreakdown.find(e => e && e.type === raceType);
      if (entry && Array.isArray(entry.breakdown)) return entry.breakdown;
      // Some legacy data stores the items flat in the array itself
      return scoreBreakdown.filter(e => e && e.type === raceType);
    }

    return [];
  };

  /** Sum of points across the breakdown items for a race type. */
  const sumBreakdownPoints = (scoreBreakdown, raceType) => {
    const items = getBreakdownItems(scoreBreakdown, raceType);
    return items.reduce((sum, b) => sum + (b?.total || 0), 0);
  };

  /** Points for a single scored item (by label prefix) within a race type. */
  const getItemPointsFor = (scoreBreakdown, raceType, labelPrefix) => {
    const items = getBreakdownItems(scoreBreakdown, raceType);
    const item = items.find(b => b?.item?.startsWith(labelPrefix));
    return item?.total || 0;
  };

  const getDriverCode = (driverId) => {
    if (!driverId) return '-';
    const driver = drivers.find(d => d.driverId === driverId);
    if (driver) return driver.code;
    if (driverId.includes('_')) {
      const parts = driverId.split('_');
      const lastPart = parts[parts.length - 1];
      const driverByLastPart = drivers.find(d => d.driverId === lastPart);
      if (driverByLastPart) return driverByLastPart.code;
      return lastPart.substring(0, 3).toUpperCase();
    }
    if (driverId.length <= 4) return driverId.toUpperCase();
    return driverId.substring(0, 3).toUpperCase();
  };

  const getTeamName = (winner) => {
    if (!winner) return '-';
    const team = teams.find(t => t.constructorId === winner);
    if (team) return team.name;
    return winner;
  };

  const buildPositionMap = (resultsArray) => {
    const map = {};
    if (!Array.isArray(resultsArray)) return map;
    resultsArray.forEach(r => {
      if (r.driverId && r.position) map[r.driverId] = parseInt(r.position);
    });
    return map;
  };

  const getPodiumIndicator = (driverId, predictedIdx, positionMap) => {
    if (!driverId) return null;
    const actualPos = positionMap[driverId];
    if (actualPos === undefined) return { symbol: '✗', cls: 'text-red-400' };
    if (actualPos === predictedIdx + 1) return { symbol: '✓', cls: 'text-green-400' };
    if (actualPos <= 10) return { symbol: '~', cls: 'text-yellow-400' };
    return { symbol: '✗', cls: 'text-red-400' };
  };

  const getSingleIndicator = (predicted, actual) => {
    if (!predicted || !actual) return null;
    if (predicted === actual) return { symbol: '✓', cls: 'text-green-400' };
    return { symbol: '✗', cls: 'text-red-400' };
  };

  useEffect(() => {
    apiClient.get('/api/f1data/drivers?season=2026')
      .then(r => setDrivers(r.data || []))
      .catch(() => {});
    apiClient.get('/api/f1data/teams?season=2026')
      .then(r => setTeams(r.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const fetchRaces = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/api/f1data/races/all');
        if (!Array.isArray(response.data)) { setError('Invalid race data'); return; }

        const allRaces = response.data
          .filter(r => r?.date && new Date(r.date).getFullYear() === 2026)
          .sort((a, b) => new Date(a.date) - new Date(b.date));

        setRaces(allRaces);

        const now = new Date();
        const completedRaces = allRaces.filter(r => new Date(r.date) < now);
        const mostRecent = completedRaces[completedRaces.length - 1];
        const target = raceId ? allRaces.find(r => r._id === raceId) : (mostRecent || allRaces[0]);
        if (target) { setSelectedRace(target); fetchRaceData(target); }
      } catch (err) {
        setError('Failed to load races.');
      } finally {
        setLoading(false);
      }
    };
    fetchRaces();
  }, [raceId]);

  const fetchRaceData = async (race) => {
    setResultsLoading(true);
    setRaceResults(null);
    setPredictions([]);
    try {
      try {
        const r = await apiClient.get(`/api/results/race/${race._id}`);
        setRaceResults(r.data);
      } catch (_) {}

      const featureClosed = arePredictionsClosed(race);
      const sprintClosed = race.isSprint && areSprintPredictionsClosed(race);

      if (featureClosed || sprintClosed) {
        try {
          const r = await apiClient.get(`/api/predictions/race/${race._id}`);
          const preds = r.data || [];

          try {
            const s = await apiClient.get(`/api/scores/race/${race._id}`);
            const scoresMap = {};
            (s.data?.scores || []).forEach(sc => {
              scoresMap[sc.user?.toString()] = {
                scoreBreakdown: sc.scoreBreakdown,
                points: sc.score
              };
            });
            preds.forEach(p => {
              const match = scoresMap[p.userId?.toString()];
              if (match) {
                p.scoreBreakdown = match.scoreBreakdown;
                p.points = match.points;
              }
            });
          } catch (_) {}

          setPredictions(preds);
        } catch (_) {}
      }
    } finally {
      setResultsLoading(false);
    }
  };

  const handleRaceSelect = (race) => {
    setSelectedRace(race);
    fetchRaceData(race);
  };

  const Cell = ({ driverId, indicator, points }) => (
    <td className="px-2 py-2 text-center text-xs text-gray-200 w-14">
      <div className="flex flex-col items-center gap-0.5">
        <span className="font-mono">{getDriverCode(driverId)}</span>
        {indicator && <span className={`font-bold leading-none ${indicator.cls}`}>{indicator.symbol}</span>}
        {points > 0 && <span className="text-yellow-400 text-xs leading-none">+{points}</span>}
      </div>
    </td>
  );

  const renderTable = (raceType) => {
    const isFeature = raceType === 'feature';

    const rows = predictions.map(p => ({
      username: p.username,
      userId: p.userId,
      preds: isFeature ? p.featurePredictions : p.sprintPredictions,
      points: sumBreakdownPoints(p.scoreBreakdown, raceType),
      scoreBreakdown: p.scoreBreakdown,
    })).filter(r => r.preds)
      .sort((a, b) => {
        if (b.points !== a.points) return b.points - a.points;
        return (a.username || '').localeCompare(b.username || '');
      });

    if (rows.length === 0) return null;

    const resultsArray = isFeature
      ? (raceResults?.results || [])
      : (raceResults?.sprintResults || []);
    const posMap = buildPositionMap(resultsArray);
    const hasResults = Object.keys(posMap).length > 0;

    const driverH2HKey = isFeature ? 'drivers' : 'sprintDrivers';
    const teamH2HKey = isFeature ? 'teams' : 'sprintTeams';

    const actualPole = isFeature ? raceResults?.polePosition : raceResults?.sprintPolePosition;
    const actualFastestLap = isFeature ? raceResults?.fastestLap : raceResults?.sprintFastestLap;
    const actualFirstRetirement = isFeature ? raceResults?.firstRetirement : raceResults?.sprintFirstRetirement;
    const actualMPG = isFeature ? raceResults?.mpg : raceResults?.sprintMPG;

    const getItemPoints = (scoreBreakdown, labelPrefix) =>
      getItemPointsFor(scoreBreakdown, raceType, labelPrefix);

    return (
      <div className="mb-8">
        <h3 className="text-base font-bold mb-3 text-gray-300">
          {isFeature ? '🏁 Feature Race' : '⚡ Sprint Race'}
        </h3>
        <div className="bg-gray-800 rounded-lg overflow-hidden shadow">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700 text-sm">
              <thead className="bg-gray-700">
                <tr>
                  <th className="sticky left-0 z-10 bg-gray-700 px-3 py-2 text-left text-xs text-gray-300 uppercase min-w-[110px] border-r border-gray-600" rowSpan={2}>User</th>
                  <th className="px-2 pt-2 pb-0 text-center text-xs text-gray-300 uppercase w-14">P1</th>
                  <th className="px-2 pt-2 pb-0 text-center text-xs text-gray-300 uppercase w-14">P2</th>
                  <th className="px-2 pt-2 pb-0 text-center text-xs text-gray-300 uppercase w-14">P3</th>
                  <th className="px-2 pt-2 pb-0 text-center text-xs text-gray-300 uppercase w-14">P4</th>
                  <th className="px-2 pt-2 pb-0 text-center text-xs text-gray-300 uppercase w-14">P5</th>
                  <th className="px-2 pt-2 pb-0 text-center text-xs text-gray-300 uppercase w-14">Pole</th>
                  <th className="px-2 pt-2 pb-0 text-center text-xs text-gray-300 uppercase w-14">Fast</th>
                  <th className="px-2 pt-2 pb-0 text-center text-xs text-gray-300 uppercase w-14">DNF</th>
                  <th className="px-2 pt-2 pb-0 text-center text-xs text-gray-300 uppercase w-14">MPG</th>
                  <th className="px-2 pt-2 pb-0 text-center text-xs text-gray-300 uppercase w-16">DR H2H</th>
                  <th className="px-2 pt-2 pb-0 text-center text-xs text-gray-300 uppercase w-16">TM H2H</th>
                  <th className="px-2 pt-2 pb-0 text-center text-xs text-gray-300 uppercase w-14">Top3</th>
                  <th className="px-2 pt-2 pb-0 text-center text-xs text-gray-300 uppercase w-14">Top5</th>
                  <th className="px-2 pt-2 pb-0 text-center text-xs text-gray-300 uppercase w-16">Score</th>
                </tr>
                {hasResults && (
                  <tr className="bg-gray-700 border-t border-gray-600">
                    {[0,1,2,3,4].map(idx => {
                      const driverId = resultsArray.find(r => parseInt(r.position) === idx + 1)?.driverId;
                      return (
                        <td key={idx} className="px-2 pb-2 pt-0 text-center text-xs text-yellow-400 font-mono font-bold w-14">
                          {driverId ? getDriverCode(driverId) : '—'}
                        </td>
                      );
                    })}
                    <td className="px-2 pb-2 pt-0 text-center text-xs text-yellow-400 font-mono font-bold w-14">
                      {actualPole ? getDriverCode(actualPole) : '—'}
                    </td>
                    <td className="px-2 pb-2 pt-0 text-center text-xs text-yellow-400 font-mono font-bold w-14">
                      {actualFastestLap ? getDriverCode(actualFastestLap) : '—'}
                    </td>
                    <td className="px-2 pb-2 pt-0 text-center text-xs text-yellow-400 font-mono font-bold w-14">
                      {actualFirstRetirement ? getDriverCode(actualFirstRetirement) : '—'}
                    </td>
                    <td className="px-2 pb-2 pt-0 text-center text-xs text-yellow-400 font-mono font-bold w-14">
                      {actualMPG ? getDriverCode(actualMPG) : '—'}
                    </td>
                    <td className="px-2 pb-2 pt-0 text-center text-xs text-yellow-400 font-mono font-bold w-16">
                      {raceResults?.headToHead?.[driverH2HKey]?.winner
                        ? getDriverCode(raceResults.headToHead[driverH2HKey].winner)
                        : '—'}
                    </td>
                    <td className="px-2 pb-2 pt-0 text-center text-xs text-yellow-400 font-bold w-16">
                      {raceResults?.headToHead?.[teamH2HKey]?.winner
                        ? getTeamName(raceResults.headToHead[teamH2HKey].winner)
                        : '—'}
                    </td>
                    <td className="px-2 pb-2 pt-0 text-center text-xs text-gray-400 w-14">—</td>
                    <td className="px-2 pb-2 pt-0 text-center text-xs text-gray-400 w-14">—</td>
                    <td className="px-2 pb-2 pt-0 text-center text-xs text-gray-400 w-16">—</td>
                  </tr>
                )}
              </thead>
              <tbody className="divide-y divide-gray-700">
                {rows.map((row, i) => {
                  const { preds } = row;
                  const isMe = currentUser && row.userId?.toString() === currentUser.id;
                  return (
                    <tr key={i} className={`${i % 2 === 0 ? 'bg-gray-800' : 'bg-gray-750'} ${isMe ? 'outline outline-2 outline-blue-400' : ''}`}>
                      <td className="sticky left-0 z-10 bg-inherit px-3 py-2 text-sm font-medium text-gray-100 border-r border-gray-600 min-w-[110px]">
                        <span className="truncate block">{row.username}{isMe && <span className="ml-1 text-xs text-blue-400">(You)</span>}</span>
                      </td>

                      {[0,1,2,3,4].map(idx => (
                        <Cell
                          key={idx}
                          driverId={preds.podium?.[idx]}
                          indicator={hasResults ? getPodiumIndicator(preds.podium?.[idx], idx, posMap) : null}
                          points={hasResults ? getItemPoints(row.scoreBreakdown, `P${idx+1}`) : 0}
                        />
                      ))}

                      {/* Pole */}
                      <Cell
                        driverId={preds.polePosition}
                        indicator={hasResults && actualPole ? getSingleIndicator(preds.polePosition, actualPole) : null}
                        points={hasResults ? getItemPoints(row.scoreBreakdown, 'Pole correct') : 0}
                      />

                      {/* Fastest Lap */}
                      <Cell
                        driverId={preds.fastestLap}
                        indicator={hasResults && actualFastestLap ? getSingleIndicator(preds.fastestLap, actualFastestLap) : null}
                        points={hasResults ? getItemPoints(row.scoreBreakdown, 'FastLap correct') : 0}
                      />

                      {/* DNF */}
                      <Cell
                        driverId={preds.firstRetirement}
                        indicator={hasResults && actualFirstRetirement ? getSingleIndicator(preds.firstRetirement, actualFirstRetirement) : null}
                        points={hasResults ? getItemPoints(row.scoreBreakdown, 'First retirement correct') : 0}
                      />

                      {/* MPG */}
                      <td className="px-2 py-2 text-center text-xs text-gray-200 w-14 font-mono">
                        <div className="flex flex-col items-center gap-0.5">
                          <span>{getDriverCode(preds.mostPlacesGained)}</span>
                          {hasResults && preds.mostPlacesGained && (() => {
                            const pts = getItemPoints(row.scoreBreakdown, 'MPG correct');
                            if (pts > 0) return (
                              <>
                                <span className="text-green-400 font-bold leading-none">✓</span>
                                <span className="text-yellow-400 leading-none">+{pts}</span>
                              </>
                            );
                            return <span className="text-red-400 font-bold leading-none">✗</span>;
                          })()}
                        </div>
                      </td>

                      {/* Driver H2H */}
                      <td className="px-2 py-2 text-center text-xs text-gray-200 w-16 font-mono">
                        <div className="flex flex-col items-center gap-0.5">
                          <span>{getDriverCode(preds.driverHeadToHead?.winner)}</span>
                          {hasResults && preds.driverHeadToHead?.winner && (() => {
                            const actualWinner = raceResults?.headToHead?.[driverH2HKey]?.winner;
                            const actualCode = getDriverCode(actualWinner);
                            const predWinner = preds.driverHeadToHead.winner;
                            const matches = predWinner === actualWinner || predWinner === actualCode;
                            const ind = matches
                              ? { symbol: '✓', cls: 'text-green-400' }
                              : actualWinner ? { symbol: '✗', cls: 'text-red-400' } : null;
                            return ind ? <span className={`font-bold leading-none ${ind.cls}`}>{ind.symbol}</span> : null;
                          })()}
                          {hasResults && (() => {
                            const pts = getItemPoints(row.scoreBreakdown, 'Driver H2H correct');
                            return pts > 0 ? <span className="text-yellow-400 leading-none">+{pts}</span> : null;
                          })()}
                        </div>
                      </td>

                      {/* Team H2H */}
                      <td className="px-2 py-2 text-center text-xs text-gray-200 w-16">
                        <div className="flex flex-col items-center gap-0.5">
                          <span>{getTeamName(preds.teamHeadToHead?.winner)}</span>
                          {hasResults && preds.teamHeadToHead?.winner && (() => {
                            const actualWinner = raceResults?.headToHead?.[teamH2HKey]?.winner;
                            const actual = getTeamName(actualWinner);
                            const pred = getTeamName(preds.teamHeadToHead?.winner);
                            const ind = getSingleIndicator(pred?.toLowerCase(), actual?.toLowerCase());
                            return ind ? <span className={`font-bold leading-none ${ind.cls}`}>{ind.symbol}</span> : null;
                          })()}
                          {hasResults && (() => {
                            const pts = getItemPoints(row.scoreBreakdown, 'Team H2H correct');
                            return pts > 0 ? <span className="text-yellow-400 leading-none">+{pts}</span> : null;
                          })()}
                        </div>
                      </td>

                      {/* Top 3 bonus */}
                      {(() => {
                        const podium = preds.podium || [];
                        const allTop3Correct = hasResults && podium.length >= 3 &&
                          [0,1,2].every(idx => posMap[podium[idx]] === idx + 1);
                        const bonusPts = hasResults ? getItemPoints(row.scoreBreakdown, 'All podium bonus') : 0;
                        return (
                          <td className="px-2 py-2 text-center text-xs w-14">
                            <div className="flex flex-col items-center gap-0.5">
                              {hasResults
                                ? <span className={allTop3Correct ? 'text-green-400 font-bold' : 'text-gray-600'}>{allTop3Correct ? '✓' : '✗'}</span>
                                : <span className="text-gray-600">—</span>}
                              {bonusPts > 0 && <span className="text-yellow-400 leading-none">+{bonusPts}</span>}
                            </div>
                          </td>
                        );
                      })()}

                      {/* Top 5 bonus */}
                      {(() => {
                        const podium = preds.podium || [];
                        const allTop5Correct = hasResults && podium.length >= 5 &&
                          [0,1,2,3,4].every(idx => posMap[podium[idx]] === idx + 1);
                        const bonusPts = hasResults ? getItemPoints(row.scoreBreakdown, 'All 5 bonus') : 0;
                        return (
                          <td className="px-2 py-2 text-center text-xs w-14">
                            <div className="flex flex-col items-center gap-0.5">
                              {hasResults
                                ? <span className={allTop5Correct ? 'text-green-400 font-bold' : 'text-gray-600'}>{allTop5Correct ? '✓' : '✗'}</span>
                                : <span className="text-gray-600">—</span>}
                              {bonusPts > 0 && <span className="text-yellow-400 leading-none">+{bonusPts}</span>}
                            </div>
                          </td>
                        );
                      })()}

                      {/* Score */}
                      <td className="px-2 py-2 text-center text-xs font-bold w-16">
                        <span className={row.points > 0 ? 'text-yellow-400' : 'text-gray-500'}>
                          {row.points > 0 ? `${row.points}` : '—'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {hasResults && (
          <div className="mt-2 flex gap-5 text-xs text-gray-400">
            <span><span className="text-green-400 font-bold">✓</span> Exact position</span>
            <span><span className="text-yellow-400 font-bold">~</span> Scored, wrong position</span>
            <span><span className="text-red-400 font-bold">✗</span> Not scored</span>
          </div>
        )}
      </div>
    );
  };

  if (loading) return <div className="container mx-auto px-4 py-8"><p className="text-center py-10">Loading races...</p></div>;
  if (error) return <div className="container mx-auto px-4 py-8"><div className="bg-red-900 text-red-200 px-4 py-3 rounded">{error}</div></div>;

  const featureClosed = arePredictionsClosed(selectedRace);
  const sprintClosed = selectedRace?.isSprint && areSprintPredictionsClosed(selectedRace);

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-2">2026 Race Results</h1>
      <p className="text-gray-400 mb-6">User predictions with scoring indicators</p>

      <div className="mb-8">
        <select
          value={selectedRace?._id || ''}
          onChange={e => { const r = races.find(x => x._id === e.target.value); if (r) handleRaceSelect(r); }}
          className="w-full px-4 py-2 border border-gray-600 rounded-lg bg-gray-800 text-gray-100"
        >
          <option value="">-- Select a race --</option>
          {races.map(race => (
            <option key={race._id} value={race._id}>
              {race.raceName} — {formatAustralianDate(race.date)}{new Date(race.date) < new Date() ? ' ✅' : ''}
            </option>
          ))}
        </select>
      </div>

      {selectedRace && (
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
            <h2 className="text-2xl font-bold">
              {selectedRace.raceName}
              <span className="text-sm font-normal text-gray-400 ml-2">{formatAustralianDate(selectedRace.date)}</span>
              {selectedRace.isSprint && <span className="ml-2 text-xs bg-purple-800 text-purple-200 px-2 py-1 rounded-full">Sprint Weekend</span>}
            </h2>
            <Link
              to={`/2026/stats/${selectedRace._id}`}
              className="inline-flex items-center gap-1.5 text-sm text-blue-300 hover:text-white bg-gray-800 hover:bg-gray-700 border border-gray-600 px-3 py-1.5 rounded-lg"
            >
              📊 Selection statistics
            </Link>
          </div>

          {resultsLoading ? (
            <div className="p-8 text-center text-gray-400">Loading...</div>
          ) : (
            <div>
              {/* Sprint section */}
              {selectedRace.isSprint && (
                sprintClosed
                  ? predictions.filter(p => p.sprintPredictions).length > 0
                    ? renderTable('sprint')
                    : <div className="p-4 bg-gray-700 text-gray-300 rounded mb-6">No sprint predictions submitted.</div>
                  : <div className="p-4 bg-blue-900 border border-blue-700 text-blue-200 rounded mb-6">
                      <p className="font-semibold">⚡ Sprint predictions still open</p>
                      <p className="text-sm mt-1">Sprint predictions will appear here once sprint qualifying begins.</p>
                    </div>
              )}

              {/* Feature section */}
              {featureClosed
                ? predictions.filter(p => p.featurePredictions).length > 0
                  ? renderTable('feature')
                  : <div className="p-4 bg-gray-700 text-gray-300 rounded">No feature predictions submitted.</div>
                : <div className="p-4 bg-blue-900 border border-blue-700 text-blue-200 rounded">
                    <p className="font-semibold">🏁 Feature predictions still open</p>
                    <p className="text-sm mt-1">Feature predictions will appear here once qualifying begins.</p>
                  </div>
              }
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Results2026;